import React, { useRef, useEffect } from 'react';

/**
 * TableFilters - Reusable filter bar component
 * 
 * Renders custom filters based on filterConfig prop.
 * Maintains focus properly (no re-render issues).
 * 
 * Example filterConfig:
 * {
 *   searchTerm: { type: 'text', placeholder: 'Search...', label: 'Search' },
 *   workType: { type: 'select', options: WORK_TYPES, label: 'Work Type' },
 *   hideIgnored: { type: 'checkbox', label: 'Hide Ignored' },
 * }
 */

export default function TableFilters({ filterConfig, filters, onFilterChange, onClearFilters }) {
  const searchInputRef = useRef(null);

  // Preserve focus on search input
  useEffect(() => {
    if (document.activeElement === searchInputRef.current) {
      searchInputRef.current?.focus();
    }
  }, [filters]);

  const handleFilterChange = (key, value) => {
    onFilterChange({ ...filters, [key]: value });
  };

  const hasActiveFilters = Object.entries(filters).some(([key, value]) => {
    if (key === 'sortBy') return false; // Ignore sort
    if (value === 'all' || value === '' || value == null) return false;
    if (key === 'hideIgnored' && value === false) return false;
    return true;
  });

  return (
    <div className="card mb-3">
      <div className="card-body">
        <h6 className="card-title mb-3">Filters</h6>
        <div className="row g-3">
          {Object.entries(filterConfig).map(([key, config]) => {
            if (config.type === 'text') {
              return (
                <div key={key} className={config.className || 'col-md-3'}>
                  <label className="form-label">{config.label}</label>
                  <input
                    ref={key === 'searchTerm' ? searchInputRef : null}
                    type="text"
                    className="form-control"
                    placeholder={config.placeholder}
                    value={filters[key] || ''}
                    onChange={e => handleFilterChange(key, e.target.value)}
                  />
                </div>
              );
            }
            
            if (config.type === 'select') {
              return (
                <div key={key} className={config.className || 'col-md-2'}>
                  <label className="form-label">{config.label}</label>
                  <select
                    className="form-select"
                    value={filters[key] || 'all'}
                    onChange={e => handleFilterChange(key, e.target.value)}
                  >
                    <option value="all">{config.allLabel || 'All'}</option>
                    {config.options.map(opt => (
                      <option key={opt.value || opt} value={opt.value || opt}>
                        {opt.label || opt}
                      </option>
                    ))}
                  </select>
                </div>
              );
            }
            
            if (config.type === 'checkbox') {
              return (
                <div key={key} className={config.className || 'col-md-2 d-flex align-items-end'}>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`filter-${key}`}
                      checked={filters[key] || false}
                      onChange={e => handleFilterChange(key, e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor={`filter-${key}`}>
                      {config.label}
                    </label>
                  </div>
                </div>
              );
            }
            
            return null;
          })}
        </div>
        
        {hasActiveFilters && (
          <div className="mt-3">
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={onClearFilters}
            >
              Clear All Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

